import React, { useEffect, useState } from "react"
import { Box, Grid, Stack, Typography, Divider, ListItemButton } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { enqueueSnackbar } from "notistack"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { getUserAccounts, ProfileQuery, sendUserAccountsConfirmationEmail, switchAccount } from "../../profileApi"
import { needsEmailConfirmation } from "../helpers/distinguishChangeUserResponse"
import { NoAccessContent } from "./NoAccessContent"
import { useAppSelector, useAppDispatch } from "../../../store/storeHooks"
import { selectUserId } from "../../../login/loginSelectors"
import { isAuthenticationComplete } from "../../../login/loginTypes"
import { useApiMutation } from "../../../../core/api/useApiMutation"
import { StandardLoadingButton } from "../../../../shared/components/buttons/LoadingButton"
import { faSpinnerThird } from "@fortawesome/pro-light-svg-icons"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { login } from "../../../login/loginSlice"
import { useNavigate } from "react-router-dom"
import { ROOT_ROUTE } from "../../../../constants/routes"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    content: {
        minHeight: "20rem",
        height: "100%",
    },
    userListContainer: {
        padding: "2rem",
    },
    listItem: {
        padding: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    switchDescriptionEmail: {
        color: "#54954b",
    },
    pendingApprovalButton: {
        color: "rgba(0, 0, 0, 0.26)",
    },
    icon: {
        padding: "0.2rem 1rem",
        color: theme.palette.tailwind.neutral[600],
    },
    groupDivider: {
        marginTop: "0.5rem",
    },
}))

export function ChangeUserAccountContent() {
    const { classes } = useStyles()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const [isEmailConfirmationSend, setIsEmailConfirmationSend] = useState(false)
    const [pendingUserName, setPendingUserName] = useState<string | null>(null)
    const userId = useAppSelector(selectUserId)

    const { data, refetch } = useApiQuery(getUserAccounts, {
        queryName: ProfileQuery.GET_USER_ACCOUNT,
        dependencies: {
            userId: userId ?? 0,
        },
        enabled: Boolean(userId),
        suspense: true,
        refetchInterval: isEmailConfirmationSend ? 2000 : undefined,
        useErrorBoundary: true,
    })

    const { mutateAsync: sendConfirmEmail, isLoading: isSendingConfirm } = useApiMutation(
        sendUserAccountsConfirmationEmail,
        {
            async onSuccess() {
                setIsEmailConfirmationSend(true)
                refetch()
            },
        },
    )

    const { mutateAsync: callChangeAccount } = useApiMutation(switchAccount, {
        async onSuccess(_params, data) {
            if (isAuthenticationComplete(data)) {
                dispatch(
                    login({
                        authToken: data.authentication.token,
                        user: data.user,
                    }),
                )

                navigate(ROOT_ROUTE)
            } else {
                enqueueSnackbar({
                    variant: "error",
                    message: l10n.getString("auth-flow-authentication-result-server-error", {
                        action: data.authenticationResult.action,
                    }),
                })
            }
        },
        async onSettled() {
            setPendingUserName(null)
        },
    })

    const groupsCount = data?.userAccountGroups?.length ?? 0

    useEffect(() => {
        if (groupsCount > 0) {
            setIsEmailConfirmationSend(false)
        }
    }, [groupsCount])

    const needsEmailConfirmationCheck = needsEmailConfirmation(data)
    const hasAccounts = groupsCount > 0
    const noAccounts = !needsEmailConfirmationCheck && !hasAccounts && data?.user

    const onUserClick = async (username: string) => {
        setPendingUserName(username)
        await callChangeAccount({ userId: userId ?? 0, username })
    }

    let content = null

    if (needsEmailConfirmationCheck && data?.authenticationResult) {
        content = (
            <NoAccessContent
                isEmailConfirmationSend={isEmailConfirmationSend}
                setIsEmailConfirmationSend={setIsEmailConfirmationSend}
                authenticationResult={{
                    user: { email: data.user.email },
                    authenticationResult: data.authenticationResult,
                }}
                userId={userId ?? 0}
            />
        )
    } else if (isEmailConfirmationSend && !hasAccounts) {
        content = (
            <Grid>
                <LocalizedStrict id="change-user-account-dialog-pending-state-email-has-been-sent">
                    <Typography variant="body1">An email has been sent. Please check your inbox.</Typography>
                </LocalizedStrict>
                <LocalizedStrict
                    id="change-user-account-dialog-pending-state-pending-approval"
                    attrs={{ loadingLabel: true }}
                >
                    <StandardLoadingButton
                        loading={isSendingConfirm}
                        loadingLabel="Pending approval..."
                        onClick={() => sendConfirmEmail({ userId: userId ?? 0 })}
                        className={classes.pendingApprovalButton}
                    >
                        Pending approval...
                    </StandardLoadingButton>
                </LocalizedStrict>
            </Grid>
        )
    } else if (noAccounts) {
        content = (
            <LocalizedStrict
                id="change-user-account-dialog-empty-content-subtitle"
                vars={{ email: data?.user.email ?? "" }}
                elems={{ email: <span className={classes.switchDescriptionEmail}>{data?.user.email}</span> }}
            >
                <Typography variant="body1">
                    No other accounts were found with the email address{" "}
                    <span className={classes.switchDescriptionEmail}>{data?.user.email}</span>
                </Typography>
            </LocalizedStrict>
        )
    } else if (hasAccounts && data?.userAccountGroups) {
        content = (
            <Stack className={classes.userListContainer} direction="column" gap="2rem">
                {data.userAccountGroups.map((group) => (
                    <Box key={group.line1}>
                        <Typography paddingBottom=".5rem" variant="subtitle1">
                            {group.line1}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {group.line2}
                        </Typography>
                        <Divider className={classes.groupDivider} />

                        {group.userAccounts.map((account) => {
                            const loading = pendingUserName === account.user.username
                            return (
                                <React.Fragment key={account.user.id}>
                                    <ListItemButton
                                        disabled={loading}
                                        onClick={() => onUserClick(account.user.username)}
                                        className={classes.listItem}
                                    >
                                        <Box display="flex">
                                            <Box>
                                                <Typography variant="body1">{account.line1}</Typography>
                                                <Typography variant="body2">{account.line2}</Typography>
                                            </Box>
                                        </Box>
                                        <Box>
                                            {loading ? (
                                                <FontAwesomeIcon icon={faSpinnerThird} spin />
                                            ) : (
                                                <FontAwesomeIcon icon={faChevronRight} />
                                            )}
                                        </Box>
                                    </ListItemButton>

                                    <Divider />
                                </React.Fragment>
                            )
                        })}
                    </Box>
                ))}
            </Stack>
        )
    }

    return <Box className={classes.content}>{content}</Box>
}

